import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
} from "@mui/material";
import {
  AccountCircle,
  Person,
  ExitToApp,
  Home,
  People,
  AdminPanelSettings,
  Logout,
  Brightness4,
  Brightness7,
  Email,
  Help,
} from "@mui/icons-material";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { getUserDisplayName } from "../utils/userUtils";
import { useSupabase } from "../providers/supabaseProvider";
import { useThemeMode } from "../providers/themeProvider";
import { useDispatch } from "react-redux";
import { clearUser } from "../features/auth/authSlice";
import { useAccessControl } from "../context/AccessControlContext";

const Header = () => {
  const { isAuthenticated, user, isLoading, logout } = useKindeAuth();

  const dispatch = useDispatch();
  const { isAdmin } = useAccessControl();

  // const { isAdmin } = useUserRole();
  const supabase = useSupabase();
  const navigate = useNavigate();
  const { mode, toggleTheme } = useThemeMode();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (path) => {
    navigate(path);
    handleClose();
  };

  const handleLogout = async () => {
    dispatch(clearUser());
    logout();
  };

  const getUserInitials = () => {
    if (!user) return "?";

    const firstName =
      user.givenName || user.given_name || user.user_metadata?.first_name;
    const lastName =
      user.familyName || user.family_name || user.user_metadata?.last_name;

    if (firstName && lastName) {
      const initials = `${firstName[0]}${lastName[0]}`.toUpperCase();
      return initials;
    }

    // Fall back to full name parsing
    const name = getUserDisplayName(user);
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    const fallback = name.charAt(0).toUpperCase();
    return fallback || "?";
  };

  if (isLoading) return null; // Don't render header while checking auth

  return (
    <AppBar position="static" sx={{ mb: 4 }}>
      <Toolbar>
        <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
          <Typography
            variant="h6"
            component="div"
            onClick={() => navigate(isAuthenticated ? "/my-record" : "/")}
            sx={{
              cursor: "pointer",
              "&:hover": { opacity: 0.8 },
            }}
          >
            PBA Ranking System
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Contact Email Button */}
          <Button
            color="inherit"
            startIcon={<Email />}
            href="mailto:support@bowlsmaster.co.za?subject=PBA%20Ranking%20Query"
            sx={{ display: { xs: "none", sm: "inline-flex" }, px: 2 }}
            data-tutorial="contact-email"
          >
            support@bowlsmaster.co.za
          </Button>

          {/* Theme Toggle Button */}
          <IconButton
            onClick={toggleTheme}
            color="inherit"
            title={
              mode === "light" ? "Switch to dark mode" : "Switch to light mode"
            }
            data-tutorial="theme-toggle"
          >
            {mode === "light" ? <Brightness4 /> : <Brightness7 />}
          </IconButton>

          {isAuthenticated ? (
            <>
              <Button
                color="inherit"
                startIcon={<Person />}
                onClick={() => navigate("/my-record")}
                sx={{
                  display: "flex",
                  fontSize: { xs: "0.75rem", sm: "1rem" },
                }}
                data-tutorial="my-ranking-button"
              >
                My Ranking
              </Button>
              <Button
                color="inherit"
                startIcon={<Home />}
                onClick={() => navigate("/pba-log")}
                sx={{
                  display: "flex",
                  fontSize: { xs: "0.75rem", sm: "1rem" },
                }}
                data-tutorial="home-button"
              >
                PBA Log
              </Button>
              {/* {isAdmin && (
                <Button
                  color="inherit"
                  startIcon={<People />}
                  onClick={() => navigate("/players")}
                  sx={{ display: { xs: "none", sm: "flex" } }}
                >
                  Players
                </Button>
              )} */}

              {/* User Menu */}
              <IconButton
                size="large"
                onClick={handleMenu}
                color="inherit"
                sx={{ ml: 1 }}
                data-tutorial="profile-menu"
              >
                <Avatar
                  src={user?.picture}
                  sx={{
                    width: 32,
                    height: 32,
                  }}
                  alt={getUserDisplayName(user)}
                />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                PaperProps={{
                  sx: { mt: 1, minWidth: 200 },
                }}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Signed in as
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {getUserDisplayName(user)}
                  </Typography>
                </Box>
                <Divider />

                {/* Mobile navigation items */}
                <MenuItem
                  onClick={() => handleMenuClick("/my-record")}
                  sx={{ display: { xs: "flex", sm: "none" } }}
                >
                  <Person sx={{ mr: 2 }} />
                  My Ranking
                </MenuItem>

                <MenuItem
                  onClick={() => handleMenuClick("/pba-log")}
                  sx={{ display: { xs: "flex", sm: "none" } }}
                >
                  <Home sx={{ mr: 2 }} />
                  PBA Log
                </MenuItem>

                {/* {isAdmin && (
                  <MenuItem
                    onClick={() => handleMenuClick("/players")}
                    sx={{ display: { xs: "flex", sm: "none" } }}
                  >
                    <People sx={{ mr: 2 }} />
                    Players
                  </MenuItem>
                )} */}

                <Divider sx={{ display: { xs: "block", sm: "none" } }} />

                <MenuItem onClick={() => handleMenuClick("/profile")}>
                  <Person sx={{ mr: 2 }} />
                  My Profile
                </MenuItem>
                {isAdmin && (
                  <MenuItem onClick={() => handleMenuClick("/admin")}>
                    <AdminPanelSettings sx={{ mr: 2 }} />
                    Admin Actions
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    localStorage.removeItem("pba_tutorial_seen");
                    window.location.reload();
                  }}
                >
                  <Help sx={{ mr: 2 }} />
                  Restart Tutorial
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    localStorage.removeItem("pba_flag_tutorial_seen");
                    window.location.reload();
                  }}
                >
                  <Help sx={{ mr: 2 }} />
                  Restart Flag Tutorial
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => handleLogout()}>
                  <ExitToApp sx={{ mr: 2 }} />
                  Sign Out
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Button
                color="inherit"
                onClick={() => navigate("/login")}
                variant="outlined"
                sx={{
                  borderColor: "rgba(255, 255, 255, 0.5)",
                  "&:hover": {
                    borderColor: "white",
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                Login
              </Button>
              <Button
                color="inherit"
                onClick={() => navigate("/signup")}
                variant="contained"
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.15)",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.25)" },
                }}
              >
                Sign Up
              </Button>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
